/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useMemo,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {Box, makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import isEmpty from 'lodash.isempty';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {Device} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import Alert from '@ion/dls/src/components/alert/Alert';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import {getDeviceIdentifierData} from '@ion/dls/src/editors/grids/DeviceDetailViewGrid';
import {BatteryIcon} from '@ion/dls/src/icons/BatteryIcon';
import {HourglassOutlinedIcon} from '@ion/dls/src/icons/HourglassOutlinedIcon';
import {SentinelHistoryIcon} from '@ion/dls/src/icons/SentinelHistoryIcon';
import {SentinelNotificationIcon} from '@ion/dls/src/icons/SentinelNotificationIcon';
import {SentinelTemperatureIcon} from '@ion/dls/src/icons/SentinelTemperatureIcon';
import {WarningIcon} from '@ion/dls/src/icons/WarningIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import useDeviceSentinelInfo from '@ion/query/src/hooks/useDeviceSentinelInfo';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';
import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
import {
  getColorBasedOnBattery,
  getDeviceIdentifierFromPayload,
  getLatestEventByType,
  getSentinelDevice,
  getTagValue
} from './helpers';
import SentinelAssociationPendingDetails from './SentinelAssociationPendingDetails';
import {
  getAssociationSummary,
  getWorkInProgressAlertProps,
  resolveIsCurrentPrimary
} from './SentinelAssociationTabUtils';
import SentinelGrid from './SentinelGrid';
import SentinelInfo from './SentinelInfo';
import SentinelTab from './SentinelTab';
import {setSecondaryDevice} from './state';

interface SentinelAssociationTabProps {
  deviceIdentifier: string | number;
  tabName?: string;
  tabIndex?: Number;
  currentDevice?: Device;
  isRefreshed?: boolean;
};

export enum SentinelDeviceType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary'
};

export enum SentinelTabType {
  CURRENT = 'current',
  OTHER_DEVICE = 'otherDevice'
};

export enum SentinelTags {
  EVENT_CODE = 'eventCode',
  DEVICE_TYPE = 'deviceType',
  BATTERY_LEVEL = 'secondary.batteryLevel',
  TEMPERATURE = 'secondary.temperature',
  SECONDARY_INSTALLATION_DATE = 'secondary.installationDate',
  SECONDARY_DEVICE_NAME = 'secondary.device_name',
  PRIMARY_DEVICE_NAME = 'primary.device_name',
  PRIMARY_INSTALLATION_DATE = 'primary.installationDate',
  TAG_REPORT_RECEIPT_TIME = 'tagReportReceiptTime',
  RAW_JSON = 'rawDeviceHexMessage',
  PRIMARY = 'ddsar-primary',
  SECONDARY = 'ddsar-secondary'
};

export enum NormalEventCode {
  CODE_11 = '11',
  CODE_22 = '22',
  ALARM_CANCEL = 'ALARM_CANCEL',
  SECONDARY_DEVICE_DETECTED = 'SECONDARY_DEVICE_DETECTED',
  PRIMARY_DEVICE_DETECTED = 'PRIMARY_DEVICE_DETECTED'
};

export enum WarningEventCode {
  CODE_10 = '10',
  SECONDARY_DEVICE_SILENT = 'SECONDARY_DEVICE_SILENT'
};

export enum AlertEventCode {
  CODE_12 = '12',
  CODE_20 = '20',
  CODE_21 = '21',
  PRIMARY_POWER_DISCONNECTED = 'PRIMARY_POWER_DISCONNECTED',
  PRIMARY_DEVICE_MISSING = 'PRIMARY_DEVICE_MISSING',
  PRIMARY_DEVICE_MISSING_CONTINUE = 'PRIMARY_DEVICE_MISSING_CONTINUE',
  ALARM_START = 'ALARM_START',
  ALARM_UPDATE = 'ALARM_UPDATE'
};

export enum DDSARServiceState {
  DISABLED = 'Disabled',
  ENABLED = 'Enabled',
  PENDING_ENABLE = 'PendingEnable',
  PENDING_DISABLE = 'PendingDisable'
};

export enum SentinelAssociationUIState {
  PENDING_DISABLE = 'pendingDisable',
  PENDING_ENABLE_CURRENT = 'pendingEnableCurrent',
  PENDING_ENABLE_ASSOCIATED = 'pendingEnableAssociated',
  ENABLED = 'enabled',
  HIDDEN = 'hidden'
};

enum DeviceCategory {
  LMU = 'LMU',
  ATU = 'ATU'
};

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    flexStyle : {
      marginTop                      : '10px',
      alignItems                     : 'center',
      [theme.breakpoints.down('sm')] : {
        flexWrap : 'wrap'
      }
    },
    detailsTab : {
      height : '100%'
    },
    noInfoStyle : {
      ...style,
      color      : 'rgba(130, 130, 130, 1)',
      fontSize   : '16px !important',
      lineHeight : '20px'
    },
    gridContainer : {
      height : 'calc(100vh - 618px)'
    },
    gridCurrentContainer : {
      height : 'calc(100vh - 525px)'
    },
    containerMargin : {
      margin : '10px 10px 27px 0'
    },
    sentinelTime : {
      marginTop : '4px',
      fontSize  : '12px',
      color     : dlsColor('grey3')
    },
    wipDetailsContainer : {
      marginTop : '16px'
    },
    spinner : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center',
      width          : '100%',
      height         : '100%',
      marginTop      : '200px'
    }
  };
});

export const style = {
  fontFamily    : 'Roboto !important',
  fontWeight    : 500,
  letterSpacing : '0px',
  verticalAlign : 'middle'
};

export const getDDSARStatus = (device): DDSARServiceState | undefined => {
  const status = device?.features?.DDSAR;
  if (Object.values(DDSARServiceState).includes(status)) {
    return status as DDSARServiceState;
  }
};

export const getSentinelAssociationUIState = (
  currentStatus?: DDSARServiceState,
  associatedStatus?: DDSARServiceState
): SentinelAssociationUIState => {
  if (currentStatus === DDSARServiceState.ENABLED) {
    if (associatedStatus === DDSARServiceState.DISABLED || associatedStatus === DDSARServiceState.ENABLED) {
      return SentinelAssociationUIState.ENABLED;
    }
  }
  if (associatedStatus === DDSARServiceState.ENABLED) {
    if (currentStatus === DDSARServiceState.DISABLED || currentStatus === DDSARServiceState.ENABLED) {
      return SentinelAssociationUIState.ENABLED;
    }
  }
  if (currentStatus === DDSARServiceState.PENDING_DISABLE || associatedStatus === DDSARServiceState.PENDING_DISABLE) {
    return SentinelAssociationUIState.PENDING_DISABLE;
  }
  if (currentStatus === DDSARServiceState.PENDING_ENABLE) {
    return SentinelAssociationUIState.PENDING_ENABLE_CURRENT;
  }
  if (associatedStatus === DDSARServiceState.PENDING_ENABLE) {
    return SentinelAssociationUIState.PENDING_ENABLE_ASSOCIATED;
  }
  return SentinelAssociationUIState.HIDDEN;
};

export const hasSentinelAssociationTab = (device): boolean => {
  const currentStatus = getDDSARStatus(device);
  return [
    DDSARServiceState.ENABLED,
    DDSARServiceState.PENDING_ENABLE,
    DDSARServiceState.PENDING_DISABLE
  ].includes(currentStatus);
};

/**
 * Displays detailed information and event history for Sentinel device associations,
 * including both the current and other (primary/secondary) devices. This component
 * supports device info display, and an event grid.
 * 
 * - Determines the active device association tabs (primary/secondary) based on the
 *   device category (LMU/ATU).
 * - For each tab, displays device-specific information (battery, temperature, installation date, etc.).
 * - Uses `getLatestEventByType` and `getDeviceIdentifier` to determine the most recent association events.
 * - Renders a `SentinelGrid` for the active tab, passing the appropriate device type and identifier.
 * - Handles tab switching and dynamic data display.
 * - Note: This component does not conditionally render itself. To achieve
 *   conditional rendering, wrap this component in a `Render.If`.
 *
 * ```
 * 
 * <SentinelAssociationTab
 *   tabName="current"
 *   currentDevice={deviceObject}
 *   sentinelInfo={{ events: eventList }}
 *   tabIndex={0}
 * />
 * 
 * ```
 * 
 */
const SentinelAssociationTab = ({
  tabName,
  currentDevice,
  deviceIdentifier,
  tabIndex = 0,
  isRefreshed = false
}: SentinelAssociationTabProps) => {
  const classes                   = useStyles();
  const dispatch                  = useDispatch();
  const identifier                = getDeviceIdentifierData(currentDevice);
  const [activeTab, setActiveTab] = useState(SentinelTabType.CURRENT);
  const currentStatus             = useMemo(() => getDDSARStatus(currentDevice), [currentDevice]);
  const associatedIdentifier      = useMemo(() => getSentinelDevice(currentDevice), [currentDevice]);
  const associatedDeviceSearch = useMemo(() =>
    associatedIdentifier ? `(esn:${associatedIdentifier})` : ''
  , [associatedIdentifier]);
  const {device: associatedDevice} = useSmartSearchDevice(
    {id: associatedDeviceSearch, ignoreFormat: true},
    !!associatedIdentifier
  );
  
  const associatedStatus = useMemo(
    () => getDDSARStatus(associatedDevice),
    [associatedDevice]
  );
  const associationState = useMemo(
    () => (
      getSentinelAssociationUIState(currentStatus, associatedStatus)
    ),
    [currentStatus, associatedStatus]
  );
  const isWorkInProgress = useMemo(
    () => ([
      SentinelAssociationUIState.PENDING_DISABLE,
      SentinelAssociationUIState.PENDING_ENABLE_CURRENT,
      SentinelAssociationUIState.PENDING_ENABLE_ASSOCIATED
    ].includes(associationState)),
    [associationState]
  );

  const hasValidAssociationState = associationState != null &&
    associationState !== SentinelAssociationUIState.HIDDEN;
  
  const isLoading = !hasValidAssociationState;
  const canLoadSentinelDetails = !isWorkInProgress;

  const {
    sentinelInfo,
    refresh: onRefreshSentinelInfo
  } = useDeviceSentinelInfo(
    deviceIdentifier,
    canLoadSentinelDetails
  );

  const refreshTab = useCallback(onRefreshSentinelInfo, []);

  useDeviceViewRefresh(refreshTab);

  const {events} = sentinelInfo || {};
  const isCurrentPrimary = useMemo(
    () => resolveIsCurrentPrimary(currentDevice),
    [currentDevice]
  );
  
  const {
    primaryESN,
    secondaryESN,
    primaryLastIdReport,
    secondaryLastIdReport,
    primaryStatus,
    secondaryStatus
  } = useMemo(() => getAssociationSummary({
    currentDevice,
    associatedDevice,
    associatedIdentifier,
    isCurrentPrimary,
    currentStatus,
    associatedStatus
  }), [
    currentDevice,
    associatedDevice,
    associatedIdentifier,
    isCurrentPrimary,
    currentStatus,
    associatedStatus
  ]);

  const handleAssociatedDeviceClick = useCallback(() => {
    if (associatedIdentifier) {
      dispatch(setSecondaryDevice(associatedIdentifier, true));
    }
  }, [dispatch, associatedIdentifier]);

  const workInProgressAlertProps = useMemo(
    () => getWorkInProgressAlertProps({associationState}),
    [associationState]
  );

  const latestPrimaryEvent = useMemo(
    () => getLatestEventByType(events, currentDevice, SentinelDeviceType.SECONDARY),
    [events, currentDevice]
  );

  const latestSecondaryEvent = useMemo(
    () => getLatestEventByType(events, currentDevice, SentinelDeviceType.PRIMARY),
    [events, currentDevice]
  );

  const isSecondarySilent     = latestSecondaryEvent?.isSilent;
  const latestPrimaryData     = latestPrimaryEvent?.latestEvent;
  const latestSecondaryData   = latestSecondaryEvent?.latestEvent;
  const secondaryFallbackData = latestSecondaryEvent?.fallbackEvent;

  const primaryIdentifier = useMemo(
    () => getDeviceIdentifierFromPayload(
      latestPrimaryData?.appMessage?.dynamicPayload,
      SentinelTags.PRIMARY_DEVICE_NAME
    ),
    [latestPrimaryData]
  );

  const secondaryIdentifier = useMemo(
    () => getDeviceIdentifierFromPayload(
      latestSecondaryData?.appMessage?.dynamicPayload,
      SentinelTags.SECONDARY_DEVICE_NAME
    ),
    [latestSecondaryData]
  );

  const tabs = useMemo(() => {
    if (isEqual(currentDevice?.category, DeviceCategory.LMU)) {
      return [
        {
          title      : translate('admin-devices:view.sentinel.currentDevice'),
          device     : latestPrimaryData,
          deviceType : SentinelDeviceType.PRIMARY,
          tabId      : SentinelTabType.CURRENT,
          identifier : identifier?.value
        },
        {
          title       : translate('admin-devices:view.sentinel.secondaryDevice'),
          device      : latestSecondaryData,
          deviceType  : SentinelDeviceType.SECONDARY,
          tabId       : SentinelTabType.OTHER_DEVICE,
          identifier  : secondaryIdentifier || getSentinelDevice(currentDevice),
          warningIcon : <WarningIcon size={IconSize.medium} />
        }
      ];
    } else if (isEqual(currentDevice?.category, DeviceCategory.ATU)) {
      return [
        {
          title      : translate('admin-devices:view.sentinel.currentDevice'),
          device     : latestSecondaryData,
          deviceType : SentinelDeviceType.SECONDARY,
          tabId      : SentinelTabType.CURRENT,
          identifier : identifier?.value
        },
        {
          title      : translate('admin-devices:view.sentinel.primaryDevice'),
          device     : latestPrimaryData,
          deviceType : SentinelDeviceType.PRIMARY,
          tabId      : SentinelTabType.OTHER_DEVICE,
          identifier : primaryIdentifier || getSentinelDevice(currentDevice)
        }
      ];
    }
  }, [
    identifier,
    currentDevice,
    primaryIdentifier,
    latestPrimaryData,
    secondaryIdentifier,
    latestSecondaryData
  ]);

  const getDisplayFields = (device, deviceType) => {
    const dynamicPayload = device?.appMessage?.dynamicPayload;
    const batteryLevel   = getTagValue(dynamicPayload, SentinelTags.BATTERY_LEVEL);
    const temperature    = getTagValue(dynamicPayload, SentinelTags.TEMPERATURE);

    const fields = [
      {
        icon  : <SentinelHistoryIcon size={IconSize.medium} color={dlsColor('grey3')} />,
        label : translate('admin-devices:view.sentinel.detailsTab.lastCommunicationDate'),
        value : formatDateTime(getTagValue(dynamicPayload, SentinelTags.TAG_REPORT_RECEIPT_TIME))
      }
    ];

    if (isEqual(deviceType, SentinelDeviceType.PRIMARY)) {
      fields.push(
        {
          icon  : <SentinelNotificationIcon size={IconSize.medium} color={dlsColor('grey3')} />,
          label : translate('admin-devices:view.sentinel.detailsTab.serviceOperationalDate'),
          value : formatDateTime(getTagValue(dynamicPayload, SentinelTags.PRIMARY_INSTALLATION_DATE))
        }
      );
    }
    if (isEqual(deviceType, SentinelDeviceType.SECONDARY)) {
      if (!isNil(batteryLevel) && !isEmpty(batteryLevel)) {
        fields.push(
          {
            icon : <BatteryIcon id={'placeholder'}
              batteryPercentage={batteryLevel}
              size={IconSize.medium}
              color={getColorBasedOnBattery(batteryLevel)} />,
            label : translate('admin-devices:view.sentinel.detailsTab.batterylevel'),
            value : `${batteryLevel}%`
          }
        );
      }
      if (!isNil(temperature) && !isEmpty(temperature)) {
        fields.push(
          {
            icon  : <SentinelTemperatureIcon size={IconSize.small} color={dlsColor('grey3')} />,
            label : translate('admin-devices:view.sentinel.detailsTab.temperature'),
            value : temperature
          }
        );
      }
      fields.push(
        {
          icon  : <SentinelNotificationIcon size={IconSize.medium} color={dlsColor('grey3')} />,
          label : translate('admin-devices:view.sentinel.detailsTab.serviceOperationalDate'),
          value : formatDateTime(getTagValue(dynamicPayload, SentinelTags.SECONDARY_INSTALLATION_DATE))
        }
      );
    }
    return fields;
  };

  const activeInfo = tabs?.find?.(tab => tab?.tabId === activeTab);

  return (
    <Render>
      <Render.If condition={isLoading}>
        <div className={classes.spinner}>
          <IonSpinner width='50px' height='50px' />
        </div>
      </Render.If>
      <Render.If condition={hasValidAssociationState && isWorkInProgress}>
        <>
          <Alert type={workInProgressAlertProps.type}
            icon={<HourglassOutlinedIcon size={24} color={workInProgressAlertProps.iconColor} />}
            title={workInProgressAlertProps.title}
            message={
              <>
                <div>
                  {workInProgressAlertProps.message}
                </div>
                <SentinelAssociationPendingDetails primary={{
                  label        : translate('admin-devices:view.sentinel.workInProgress.primary'),
                  esn          : primaryESN,
                  lastIdReport : primaryLastIdReport,
                  status       : primaryStatus,
                  isClickable  : !isCurrentPrimary && !!associatedIdentifier &&
                                 !isUndefined(associatedStatus) && !isUndefined(currentStatus) &&
                                 associatedStatus !== DDSARServiceState.DISABLED &&
                                 currentStatus !== DDSARServiceState.DISABLED,
                  onClick : handleAssociatedDeviceClick
                }}
                secondary={{
                  label        : translate('admin-devices:view.sentinel.workInProgress.secondary'),
                  esn          : secondaryESN,
                  lastIdReport : secondaryLastIdReport,
                  status       : secondaryStatus,
                  isClickable  : isCurrentPrimary && !!associatedIdentifier &&
                                 !isUndefined(associatedStatus) && !isUndefined(currentStatus) &&
                                 associatedStatus !== DDSARServiceState.DISABLED &&
                                 currentStatus !== DDSARServiceState.DISABLED,
                  onClick : handleAssociatedDeviceClick
                }} />
              </>
            }
            gutters={false}
            placement='bottom' />
        </>
      </Render.If>
      <Render.If condition={hasValidAssociationState && !isWorkInProgress}>
        <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab}>
          <div className={classes.flexStyle}>
            <Box sx={{p: {xs: 2, sm: 3}}}>
              <Box sx={{margin: 'auto', borderRadius: 2, boxShadow: 1, overflow: 'hidden'}}>
                <Box sx={{
                  display       : 'flex',
                  flexDirection : {xs: 'column', sm: 'row'},
                  bgcolor       : 'rgba(242, 242, 242, 1)',
                  flexShrink    : 0
                }}>
                  {tabs?.map((tab) => (
                    <SentinelTab key={tab?.title}
                      deviceData={tab}
                      activeTabId={activeTab}
                      setActiveTabId={setActiveTab}
                      currentTabIdentifier={identifier?.label}
                      isSecondarySilent={isSecondarySilent} />
                  ))}
                </Box>
                <Box sx={{p: {xs: 2, sm: 3}, pt: {xs: 2, sm: 3}, bgcolor: dlsColor('white')}}>
                  <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
                    {activeInfo?.device && (() => {
                      const fields = getDisplayFields(
                        isSecondarySilent && activeInfo?.deviceType === SentinelDeviceType.SECONDARY
                          ? secondaryFallbackData
                          : activeInfo?.device,
                        activeInfo?.deviceType
                      ) || [];

                      const lastCommunicationDate = fields?.find((fieldEntry) => (
                        fieldEntry?.label === translate(translate(
                          'admin-devices:view.sentinel.detailsTab.lastCommunicationDate'
                        ))
                      ))?.value;

                      return fields.map(field => (
                        <SentinelInfo key={field?.label}
                          icon={field?.icon}
                          label={field?.label}
                          value={field?.value}
                          lastUpdateDate={lastCommunicationDate}
                          isSecondarySilent={
                            isSecondarySilent &&
                          activeInfo?.deviceType === SentinelDeviceType.SECONDARY
                          } />
                      ));
                    })()}
                  </Box>
                  <div className={classnames(activeTab === SentinelTabType.CURRENT
                    ? classes.gridCurrentContainer : classes.gridContainer, classes.containerMargin)}>
                    <SentinelGrid tabName={tabName}
                      activeTab={activeTab}
                      isRefreshed={isRefreshed}
                      deviceType={activeInfo?.deviceType}
                      currentIdentifier={activeInfo?.identifier} />
                  </div>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
      </Render.If>
    </Render>
  );
};

export default React.memo(SentinelAssociationTab);
